import { useRef, useState } from "react";
import { api } from "@/lib/api";

/**
 * PATCH an event to a new start/end. Caller updates UI optimistically.
 */
export async function rescheduleEvent(familyId, eventId, startAt, endAt) {
  return api.patch(`/families/${familyId}/events/${eventId}`, {
    start_at: startAt,
    end_at: endAt,
  });
}

/**
 * Long-press + pointer-drag hook that works on touch AND mouse.
 *
 * Usage:
 *   const drag = useLongPressDrag({ payload: { eventId: e.id }, onDrop });
 *   <div {...drag.bind} />  // attach to the source element
 *
 * onDrop receives ({ payload, dropTarget: HTMLElement, x, y }).
 * The dropTarget is the nearest ancestor of the release point that has
 * data-drop-target=… attribute. Returns null if no valid target.
 */
export function useLongPressDrag({ payload, onDrop, holdMs = 320, label }) {
  const [dragging, setDragging] = useState(false);
  const ghostRef = useRef(null);
  const timerRef = useRef(null);
  const startRef = useRef({ x: 0, y: 0 });
  const movedRef = useRef(false);
  // ⚠️ DO NOT REMOVE this ref. We MUST track and explicitly release the
  // pointer capture set in onPointerDown, otherwise the pointer remains
  // captured on the source element after release. On iOS WKWebView this
  // breaks every subsequent tap on a sibling element (TabBar, top sub-
  // tabs, MoreSheet items that navigate) — the touch is routed back to
  // the captured element instead of the actual target. Symptom: nav
  // appears "frozen" on Plan / Activities / Whereabouts after touching
  // a draggable calendar event. Regression history: shipped without
  // release between Batch 13 and Batch 53. Keep the explicit release.
  const capturedRef = useRef({ el: null, id: null });

  const releaseCapture = () => {
    const { el, id } = capturedRef.current;
    if (el && id != null) {
      try { el.releasePointerCapture?.(id); } catch { /* noop */ }
    }
    capturedRef.current = { el: null, id: null };
  };

  const cleanup = () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (ghostRef.current) {
      try { document.body.removeChild(ghostRef.current); } catch { /* noop */ }
      ghostRef.current = null;
    }
    releaseCapture();
    setDragging(false);
  };

  const moveGhost = (x, y) => {
    if (!ghostRef.current) return;
    ghostRef.current.style.left = `${x - 80}px`;
    ghostRef.current.style.top = `${y - 18}px`;
  };

  const spawnGhost = (x, y, srcEl) => {
    const ghost = document.createElement("div");
    ghost.setAttribute("data-testid", "drag-ghost");
    ghost.style.cssText = `
      position: fixed; left: ${x - 80}px; top: ${y - 18}px; width: 160px;
      background: hsl(12 69% 62%); color: white; padding: 8px 12px; border-radius: 12px;
      font-size: 12px; font-weight: 500; pointer-events: none; z-index: 9999;
      box-shadow: 0 10px 28px -4px rgba(0,0,0,0.35); transform: rotate(-2deg);
      text-shadow: 0 1px 1px rgba(0,0,0,.3); overflow: hidden; text-overflow: ellipsis;
      white-space: nowrap;
    `;
    ghost.textContent = label || "Moving…";
    document.body.appendChild(ghost);
    ghostRef.current = ghost;
    if (srcEl) srcEl.style.opacity = "0.4";
  };

  const onPointerDown = (e) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    startRef.current = { x: e.clientX, y: e.clientY };
    movedRef.current = false;
    const srcEl = e.currentTarget;
    // setPointerCapture routes every subsequent pointer event to srcEl
    // until releasePointerCapture is called (or the element unmounts).
    // We MUST release it on pointerup/cancel — see capturedRef comment.
    try {
      srcEl.setPointerCapture?.(e.pointerId);
      capturedRef.current = { el: srcEl, id: e.pointerId };
    } catch { /* noop */ }
    timerRef.current = setTimeout(() => {
      setDragging(true);
      spawnGhost(startRef.current.x, startRef.current.y, srcEl);
      if (navigator.vibrate) { try { navigator.vibrate(8); } catch { /* noop */ } }
    }, holdMs);
  };

  const onPointerMove = (e) => {
    const dx = e.clientX - startRef.current.x;
    const dy = e.clientY - startRef.current.y;
    if (!dragging && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
      movedRef.current = true;
      // Movement before long-press fires → treat as scroll, abort drag arm.
      // Release the pointer capture immediately so the scroll gesture can
      // re-target the scroll container (and so subsequent taps on siblings
      // like the TabBar are NOT routed to this captured element).
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
      releaseCapture();
      return;
    }
    if (dragging) {
      moveGhost(e.clientX, e.clientY);
      e.preventDefault();
    }
  };

  const onPointerUp = (e) => {
    const wasDragging = dragging;
    const srcEl = e.currentTarget;
    if (srcEl) srcEl.style.opacity = "";
    cleanup();
    if (!wasDragging) return;
    e.preventDefault();
    // Hide ghost so elementFromPoint hits the real target
    const target = document.elementFromPoint(e.clientX, e.clientY);
    const dropEl = target?.closest("[data-drop-target]");
    if (dropEl && onDrop) {
      onDrop({ payload, dropTarget: dropEl, x: e.clientX, y: e.clientY });
    }
  };

  const onPointerCancel = () => cleanup();

  return {
    dragging,
    bind: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel,
      style: { touchAction: "manipulation" },
    },
  };
}
